import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Download,
  Eye,
  FileText,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from "lucide-react";
import UserAvatar from "../ui/UserAvatar";
import AttachmentViewer from "../chat/AttachmentViewer";
import { resolveFileUrl } from "../../utils/fileUrl";
import {
  acknowledgeNotice,
  deleteNotice,
  fetchNotice,
  fetchNoticeAttachmentUrl,
  markNoticeRead,
  setNoticePinned,
} from "../../controllers/noticeController";
import { NoticeBody, NoticeChips, NoticeModal, noticeButton } from "./NoticeParts";
import NoticeGallery from "./NoticeGallery";
import NoticeReceipts from "./NoticeReceipts";
import {
  NOTICE_CATEGORIES,
  NOTICE_STATUS,
  formatFileSize,
  formatNoticeDateTime,
  formatNoticeTime,
  isImage,
  isPdf,
  isViewable,
  needsAck,
} from "./noticeUtils";

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

/** HR/admin actions behind the "⋯" button in the post header. */
function ManageMenu({ notice, onEdit, onTogglePin, onDelete, busy }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const item = "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-app-hover disabled:opacity-50";
  const run = (fn) => () => {
    setOpen(false);
    fn();
  };
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={noticeButton.icon}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notice options"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-5 w-5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-48 overflow-hidden rounded-xl border border-app-primary bg-app-card py-1 text-app-primary shadow-xl dark:shadow-black/40">
          <button type="button" className={item} onClick={run(onEdit)}>
            <Pencil className="h-4 w-4 text-app-muted" /> Edit notice
          </button>
          <button type="button" className={item} onClick={run(onTogglePin)} disabled={busy}>
            {notice.isPinned ? <PinOff className="h-4 w-4 text-app-muted" /> : <Pin className="h-4 w-4 text-app-muted" />}
            {notice.isPinned ? "Unpin" : "Pin to top"}
          </button>
          <button type="button" className={`${item} text-red-600 dark:text-red-400`} onClick={run(onDelete)}>
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/** Files that aren't shown in the gallery: PDFs open in the viewer, everything else downloads. */
function FileList({ files, onView }) {
  if (!files.length) return null;
  return (
    <ul className="space-y-2 px-4 pt-3 sm:px-5">
      {files.map((file) => {
        const viewable = isViewable(file.mimeType);
        const content = (
          <>
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold ${
                isPdf(file.mimeType)
                  ? "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300"
                  : "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-sky-300"
              }`}
            >
              {isPdf(file.mimeType) ? "PDF" : <FileText className="h-5 w-5" aria-hidden="true" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-app-primary">{file.fileName}</span>
              <span className="block text-xs text-app-muted">
                {formatFileSize(file.size)} · {viewable ? "Click to view" : "Click to download"}
              </span>
            </span>
          </>
        );
        return (
          <li key={file.id} className="flex items-center gap-2 rounded-xl border border-app-primary pr-2 transition-colors hover:bg-app-hover">
            {viewable ? (
              <button type="button" onClick={() => onView(file.id)} className="flex min-w-0 flex-1 items-center gap-3 p-2.5 text-left">
                {content}
              </button>
            ) : (
              <a href={resolveFileUrl(file.downloadUrl)} className="flex min-w-0 flex-1 items-center gap-3 p-2.5">
                {content}
              </a>
            )}
            <a href={resolveFileUrl(file.downloadUrl)} className={noticeButton.icon} aria-label={`Download ${file.fileName}`} title="Download">
              <Download className="h-4 w-4" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A notice shown like a social media post: author header, text, image gallery, files, then seen/acknowledged counts
 * and actions. Images and PDFs open in the in-app viewer. HR/admins get edit/pin/delete and read receipts.
 */
function NoticeDetailModal({ noticeId, canManage, version, onClose, onEdit, onChanged }) {
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // "ack" | "pin" | "delete"
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [view, setView] = useState("post"); // post | receipts
  const [viewerId, setViewerId] = useState(null);

  const load = useCallback(async () => {
    try {
      let data = await fetchNotice(noticeId);
      if (!data.readAt && data.status === "active") {
        data = await markNoticeRead(noticeId);
        onChanged();
      }
      setNotice(data);
    } catch (err) {
      setError(err?.response?.status === 404 ? "This notice is no longer available." : "Couldn't load this notice.");
    }
  }, [noticeId, onChanged]);

  useEffect(() => {
    load();
  }, [load]);

  // Another change arrived (an edit, or someone read/acknowledged it): refresh quietly.
  const loaded = Boolean(notice);
  useEffect(() => {
    if (!loaded) return;
    fetchNotice(noticeId).then(setNotice).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const run = async (kind, action, fallback) => {
    setBusy(kind);
    setError("");
    try {
      await action();
      onChanged();
    } catch (err) {
      setError(errorMessage(err, fallback));
    } finally {
      setBusy(null);
    }
  };

  const acknowledge = () =>
    run("ack", async () => setNotice(await acknowledgeNotice(noticeId)), "Couldn't record your acknowledgement.");
  const togglePin = () =>
    run("pin", async () => setNotice(await setNoticePinned(noticeId, !notice.isPinned)), "Couldn't update the pin.");
  const remove = () =>
    run(
      "delete",
      async () => {
        await deleteNotice(noticeId);
        onClose();
      },
      "Couldn't delete the notice."
    );

  // Escape closes the image viewer first, not the whole notice.
  const close = () => {
    if (!viewerId) onClose();
  };

  const attachments = notice?.attachments || [];
  const images = attachments.filter((a) => isImage(a.mimeType));
  const files = attachments.filter((a) => !isImage(a.mimeType));
  // The viewer pages through images first, then PDFs, in the order they appear in the post.
  const viewerItems = [...images, ...files.filter((a) => isPdf(a.mimeType))].map((attachment) => ({ attachment }));
  const category = notice && (NOTICE_CATEGORIES[notice.category] || NOTICE_CATEGORIES.general);
  const CategoryIcon = category?.icon;
  const ackPending = notice && needsAck(notice);
  const edited = notice && new Date(notice.updatedAt) - new Date(notice.createdAt) > 60000;
  const showCounts = (canManage && notice?.stats) || notice?.requiresAck;

  return (
    <NoticeModal title={notice?.title || "Notice"} onClose={close} width="max-w-xl" bare>
      {!notice && (
        <div className="p-5">
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className={noticeButton.icon} aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
          {error ? (
            <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">{error}</p>
          ) : (
            <div className="space-y-3" aria-hidden="true">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 animate-pulse rounded-full bg-app-hover" />
                <div className="h-4 w-40 animate-pulse rounded bg-app-hover" />
              </div>
              <div className="h-6 w-2/3 animate-pulse rounded bg-app-hover" />
              <div className="h-24 animate-pulse rounded bg-app-hover" />
              <div className="h-56 animate-pulse rounded-xl bg-app-hover" />
            </div>
          )}
        </div>
      )}

      {notice && view === "receipts" && (
        <div>
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-app-primary bg-app-card px-3 py-2.5">
            <button type="button" onClick={() => setView("post")} className={noticeButton.icon} aria-label="Back to notice">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-app-heading">Read receipts</p>
              <p className="truncate text-xs text-app-muted">{notice.title}</p>
            </div>
            <button type="button" onClick={onClose} className={noticeButton.icon} aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4 sm:p-5">
            <NoticeReceipts noticeId={notice.id} requiresAck={notice.requiresAck} version={version} />
          </div>
        </div>
      )}

      {notice && view === "post" && (
        <article aria-label={notice.title}>
          {/* Author header */}
          <header className="flex items-start gap-3 px-4 pt-4 sm:px-5">
            <UserAvatar name={notice.author?.name || "HR"} src={notice.author?.avatarThumbUrl} seed={notice.author?.id} size="md" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold leading-tight text-app-heading">
                {notice.author?.name || "HR"}
                {notice.author?.jobTitle && <span className="font-normal text-app-muted"> · {notice.author.jobTitle}</span>}
              </p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-app-muted">
                <span title={formatNoticeDateTime(notice.publishAt)}>
                  {notice.status === "scheduled"
                    ? `Goes live ${formatNoticeDateTime(notice.publishAt)}`
                    : formatNoticeTime(notice.publishAt)}
                </span>
                {edited && <span>· Edited</span>}
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1">
                  <CategoryIcon className="h-3.5 w-3.5" aria-hidden="true" /> {category.label}
                </span>
                {canManage && notice.status !== "active" && (
                  <span className={`ml-1 rounded-full px-1.5 py-px text-[10px] font-semibold ${NOTICE_STATUS[notice.status].chip}`}>
                    {NOTICE_STATUS[notice.status].label}
                  </span>
                )}
              </p>
            </div>
            <div className="-mr-2 -mt-1 flex shrink-0 items-center">
              {canManage && (
                <ManageMenu
                  notice={notice}
                  busy={busy === "pin"}
                  onEdit={() => onEdit(notice)}
                  onTogglePin={togglePin}
                  onDelete={() => setConfirmDelete(true)}
                />
              )}
              <button type="button" onClick={onClose} className={noticeButton.icon} aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
          </header>

          {/* Text */}
          <div className="px-4 pt-3 sm:px-5">
            {(notice.isPinned || notice.priority !== "normal") && (
              <NoticeChips notice={notice} showCategory={false} className="mb-2" />
            )}
            <h2 className="text-lg font-semibold leading-snug text-app-heading">{notice.title}</h2>
            <NoticeBody body={notice.body} className="mt-1.5 text-[15px]" />
          </div>

          {/* Media */}
          {images.length > 0 && (
            <div className="mt-3 border-y border-app-primary">
              <NoticeGallery images={images} onOpen={setViewerId} />
            </div>
          )}
          <FileList files={files} onView={setViewerId} />

          {notice.expiresAt && (
            <p className="flex items-center gap-1.5 px-4 pt-3 text-xs text-app-muted sm:px-5">
              <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
              {notice.status === "expired" ? "Expired" : "Visible until"} {formatNoticeDateTime(notice.expiresAt)}
            </p>
          )}

          {error && (
            <p className="mx-4 mt-3 rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error sm:mx-5">{error}</p>
          )}

          {/* Engagement counts */}
          {showCounts ? (
            <div className="mx-4 mt-3 flex items-center justify-between gap-3 border-b border-app-primary pb-2.5 text-xs text-app-muted sm:mx-5">
              {canManage && notice.stats ? (
                <button
                  type="button"
                  onClick={() => setView("receipts")}
                  className="inline-flex items-center gap-3 hover:text-app-primary hover:underline"
                >
                  <span className="inline-flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Seen by {notice.stats.readCount} of {notice.stats.audience}
                  </span>
                  {notice.requiresAck && (
                    <span className="inline-flex items-center gap-1">
                      <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> {notice.stats.ackCount} acknowledged
                    </span>
                  )}
                </button>
              ) : (
                <span />
              )}
              {notice.requiresAck && (
                <span className="inline-flex items-center gap-1 font-medium text-amber-700 dark:text-amber-300">
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Acknowledgement required
                </span>
              )}
            </div>
          ) : (
            <div className="h-4" />
          )}

          {/* Actions */}
          {confirmDelete ? (
            <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 sm:px-5">
              <span className="mr-auto text-sm text-app-secondary">Delete this notice for everyone?</span>
              <button type="button" className={noticeButton.secondary} onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
              <button type="button" className={noticeButton.danger} onClick={remove} disabled={busy === "delete"}>
                {busy === "delete" ? "Deleting…" : "Delete"}
              </button>
            </div>
          ) : (
            (notice.requiresAck || canManage) && (
              <div className="flex gap-1 px-2 py-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))] sm:px-3">
                {notice.requiresAck &&
                  (notice.acknowledgedAt ? (
                    <span
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400"
                      title={`Acknowledged ${formatNoticeDateTime(notice.acknowledgedAt)}`}
                    >
                      <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> Acknowledged
                    </span>
                  ) : ackPending ? (
                    <button
                      type="button"
                      onClick={acknowledge}
                      disabled={busy === "ack"}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#2470B7] py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-500"
                    >
                      <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                      {busy === "ack" ? "Saving…" : "I have read and understood"}
                    </button>
                  ) : null)}
                {canManage && (
                  <button
                    type="button"
                    onClick={() => setView("receipts")}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold text-app-secondary transition-colors hover:bg-app-hover"
                  >
                    <Users className="h-5 w-5" aria-hidden="true" /> Read receipts
                  </button>
                )}
              </div>
            )
          )}
        </article>
      )}

      {viewerId && notice && (
        <AttachmentViewer
          items={viewerItems}
          startId={viewerId}
          onClose={() => setViewerId(null)}
          fetchLinks={fetchNoticeAttachmentUrl}
          describe={() => ({
            userId: notice.author?.id,
            name: notice.author?.name || "HR",
            avatarUrl: notice.author?.avatarThumbUrl,
            at: notice.publishAt,
          })}
        />
      )}
    </NoticeModal>
  );
}

export default NoticeDetailModal;
